import { useEffect, useMemo, useRef } from 'react';
import { ReactFlow, useReactFlow, type EdgeTypes, type NodeTypes, type Viewport } from '@xyflow/react';
import { useDependencias, useQuadro } from '../../application/dependencias';
import { interpretarRoda, zoomEm, ZOOM_MAXIMO, ZOOM_MINIMO } from '../../domain/camera';
import { ArestaSeta } from '../notas/ArestaSeta';
import type { ArestaSetaTipo } from '../notas/montar-nos-notas';
import { NoNota } from '../notas/NoNota';
import { useFocoNaNota } from '../notas/use-foco-nota';
import { usePanComCtrl, useTeclasDoQuadro } from '../notas/use-teclas-quadro';
import type { Ferramenta } from './BarraFerramentas';
import { useAcoesEstado } from './AcoesEstado';
import { Controles } from './Controles';
import { uuidDoNo } from './montar-nos';
import { NoCartao } from './NoCartao';
import { NoGrupo } from './NoGrupo';
import { useInteracaoQuadro } from './use-interacao-quadro';
import { useNosQuadro, type NoDoQuadro } from './use-nos-quadro';
import { useSolturaNoQuadro } from './use-soltura-arquivos';

const TIPOS: NodeTypes = { grupo: NoGrupo, cartao: NoCartao, nota: NoNota };
const TIPOS_ARESTA: EdgeTypes = { seta: ArestaSeta };
const SENSIBILIDADE = { mouse: 0.0015, pinca: 0.01 };

const classeDoQuadro = (e: { mover: boolean; criando: boolean; emArraste: boolean }) =>
  ['quadro', e.mover && 'quadro--mao', e.criando && 'quadro--criando', e.emArraste && 'quadro--arrastando'].filter(Boolean).join(' ');

/** Cartão selecionado + Delete: vai para a Lixeira pelo mesmo caminho do menu (pede o motivo e confirma). */
function useLixeiraPeloTeclado() {
  const { enviarLixeira } = useAcoesEstado();
  const processos = useQuadro((s) => s.dados?.processos);
  return (uuid: string) => {
    const processo = processos?.find((p) => p.uuid === uuid);
    if (processo) void enviarLixeira(processo);
  };
}

/** Roda do mouse = zoom no cursor; dois dedos no trackpad = mover; pinça = zoom (RQ-QDR-002). */
function useRodaDoQuadro(alvo: React.RefObject<HTMLDivElement | null>) {
  const { getViewport, setViewport } = useReactFlow();
  useEffect(() => {
    const elemento = alvo.current;
    if (!elemento) return;
    const aoRolar = (evento: WheelEvent) => {
      evento.preventDefault();
      evento.stopPropagation();
      const camera = getViewport();
      if (interpretarRoda(evento) === 'mover') {
        setViewport({ ...camera, x: camera.x - evento.deltaX, y: camera.y - evento.deltaY });
        return;
      }
      const caixa = elemento.getBoundingClientRect();
      const fator = Math.exp(-evento.deltaY * (evento.ctrlKey ? SENSIBILIDADE.pinca : SENSIBILIDADE.mouse));
      setViewport(zoomEm(camera, { x: evento.clientX - caixa.left, y: evento.clientY - caixa.top }, fator));
    };
    elemento.addEventListener('wheel', aoRolar, { passive: false, capture: true });
    return () => elemento.removeEventListener('wheel', aoRolar, { capture: true });
  }, [alvo, getViewport, setViewport]);
}

type Props = {
  ferramenta: Ferramenta;
  definirFerramenta: (f: Ferramenta) => void;
  /** Ícone da Lixeira: a tampa abre enquanto um cartão arrastado passa por cima (RQ-EST-010). */
  lixeira: React.RefObject<HTMLElement | null>;
  abrirLixeira: (aberta: boolean) => void;
  /** Duplo clique no cartão abre a licitação (RQ-QDR-003). */
  abrirProcesso: (processoUuid: string) => void;
};

export function Quadro({ ferramenta, definirFerramenta, lixeira, abrirLixeira, abrirProcesso }: Props) {
  const { sessao, armazenamento } = useDependencias();
  const dados = useQuadro((s) => s.dados);
  const definirZoom = useQuadro((s) => s.definirZoom);
  const { nos, arestas, aoMudarNos, aoMudarArestas, voltar, alvos } = useNosQuadro();
  const interacao = useInteracaoQuadro({ ferramenta, definirFerramenta, lixeira, abrirLixeira, voltar, alvos });
  useFocoNaNota(nos);
  const alvo = useRef<HTMLDivElement>(null);
  // Câmera de cada pessoa neste navegador (ADR-0023): a chave leva quem está conectado.
  const chaveCamera = `${sessao.usuarioUuid}:${sessao.quadroUuid}`;
  const cameraSalva = useMemo(() => armazenamento.lerCamera(chaveCamera), [armazenamento, chaveCamera]);
  const { getViewport } = useReactFlow();
  useRodaDoQuadro(alvo);

  // Câmera restaurada do dispositivo: o zoom inicial define o nível de detalhe sem esperar um movimento.
  useEffect(() => definirZoom(getViewport().zoom), [definirZoom, getViewport, dados]);

  const soltura = useSolturaNoQuadro(interacao.receberDaBandeja);
  const { mao, apagarSetas } = useTeclasDoQuadro<NoDoQuadro, ArestaSetaTipo>(ferramenta, useLixeiraPeloTeclado());
  const ctrl = usePanComCtrl(alvo);
  const criando = ferramenta === 'nota' || ferramenta === 'conector';
  const classe = classeDoQuadro({ mover: mao || ctrl, criando, emArraste: useQuadro((s) => s.emArraste) });

  return (
    <div ref={alvo} className={classe} {...soltura}>
      <ReactFlow<NoDoQuadro, ArestaSetaTipo>
        nodes={nos}
        edges={arestas}
        onNodesChange={aoMudarNos}
        onEdgesChange={aoMudarArestas}
        nodeTypes={TIPOS}
        edgeTypes={TIPOS_ARESTA}
        onNodeDragStart={interacao.aoComecar}
        onNodeDrag={interacao.aoArrastar}
        onNodeDragStop={interacao.aoSoltar}
        onNodeClick={interacao.aoClicarNo}
        onPaneClick={interacao.aoClicarNoVazio}
        onNodeDoubleClick={(_, no) => no.type === 'cartao' && abrirProcesso(uuidDoNo(no.id))}
        onMove={(_, camera) => definirZoom(camera.zoom)}
        onMoveEnd={(_, camera: Viewport) => armazenamento.salvarCamera(chaveCamera, camera)}
        defaultViewport={cameraSalva ?? undefined}
        fitView={!cameraSalva}
        fitViewOptions={{ padding: 0.08 }}
        minZoom={ZOOM_MINIMO}
        maxZoom={ZOOM_MAXIMO}
        zoomOnScroll={false}
        panOnScroll={false}
        zoomOnDoubleClick={false}
        panOnDrag={mao ? true : [1]}
        selectionOnDrag={!mao && !criando}
        nodesDraggable={!mao && !criando}
        nodesConnectable={false}
        elementsSelectable={!mao}
        nodeDragThreshold={4}
        multiSelectionKeyCode="Shift"
        {...apagarSetas}
        proOptions={{ hideAttribution: true }}
        aria-label="Quadro de licitações"
      >
        <Controles />
      </ReactFlow>
    </div>
  );
}
